import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEME_COLORS = {
  blue: { "500": "#3B82F6", "600": "#2563EB", "50": "#EFF6FF" },
  emerald: { "500": "#10B981", "600": "#059669", "50": "#ECFDF5" },
  violet: { "500": "#8B5CF6", "600": "#7C3AED", "50": "#F5F3FF" },
  rose: { "500": "#F43F5E", "600": "#E11D48", "50": "#FFF1F2" },
  orange: { "500": "#F97316", "600": "#EA580C", "50": "#FFF7ED" },
  cyan: { "500": "#06B6D4", "600": "#0891B2", "50": "#ECFEFF" },
  amber: { "500": "#F59E0B", "600": "#D97706", "50": "#FFFBEB" },
  indigo: { "500": "#6366F1", "600": "#4F46E5", "50": "#EEF2FF" },
  pink: { "500": "#EC4899", "600": "#DB2777", "50": "#FDF2F8" },
  teal: { "500": "#14B8A6", "600": "#0D9488", "50": "#F0FDFA" },
};

const DEFAULT = {
  theme_color: "blue", app_name: "Pipe4.me", app_subtitle: "Seu pipeline, sob controle.",
  logo_url: "", sidebar_mode: "light", colors: THEME_COLORS.blue,
  default_ddi: "55", ddi_country: "BR", phone_mask: "(##) #####-####",
  tipo_negocio_padrao: "recorrente_setup",
};

function hexToHsl(hex) {
  let r = parseInt(hex.slice(1, 3), 16) / 255;
  let g = parseInt(hex.slice(3, 5), 16) / 255;
  let b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function applyThemeCss(colors) {
  if (colors?.["500"]) document.documentElement.style.setProperty("--primary", hexToHsl(colors["500"]));
}

const AppearanceContext = createContext(null);

export { THEME_COLORS };

export function AppearanceProvider({ children }) {
  const { user } = useAuth();
  const [appearance, setAppearance] = useState(DEFAULT);
  const [loaded, setLoaded] = useState(false);

  const fetchAppearance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/appearance`, { withCredentials: true });
      const data = { ...DEFAULT, ...res.data, colors: THEME_COLORS[res.data.theme_color] || THEME_COLORS.blue };
      setAppearance(data);
      document.title = "Pipe4.me";
      applyThemeCss(data.colors);
    } catch {
      // Not authenticated yet, use defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  // Re-fetch whenever the authenticated user changes (e.g. after login)
  useEffect(() => {
    if (user) fetchAppearance();
  }, [user, fetchAppearance]);

  // Initial load attempt (may fail if not yet authenticated)
  useEffect(() => { fetchAppearance(); }, [fetchAppearance]);

  const updateAppearance = async (updates) => {
    const newAppearance = { ...appearance, ...updates };
    if (updates.theme_color) newAppearance.colors = THEME_COLORS[updates.theme_color] || THEME_COLORS.blue;
    setAppearance(newAppearance);
    document.title = "Pipe4.me";
    applyThemeCss(newAppearance.colors);
    try { await axios.put(`${API}/appearance`, updates, { withCredentials: true }); } catch (err) { console.error(err); }
  };

  return (
    <AppearanceContext.Provider value={{ ...appearance, updateAppearance, fetchAppearance, loaded }}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext);
  if (!ctx) return DEFAULT;
  return ctx;
}
